'use client';

import React, { useCallback, useState } from 'react';
import { Upload, FileText, AlertCircle, Linkedin } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface FileUploadZoneProps {
  onFileAccepted: (file: File) => void;
  isLoading: boolean;
}

export default function FileUploadZone({ onFileAccepted, isLoading }: FileUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragError, setDragError] = useState('');

  const validateFile = (file: File): string => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/csv',
      'text/plain',
    ];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!validExtensions.includes(ext) && !validTypes.includes(file.type)) {
      return 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)';
    }
    if (file.size > 10 * 1024 * 1024) {
      return 'File size must be under 10MB';
    }
    return '';
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const err = validateFile(file);
      if (err) { setDragError(err); return; }
      setDragError('');
      onFileAccepted(file);
    },
    [onFileAccepted]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setDragError(err); return; }
    setDragError('');
    onFileAccepted(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* LinkedIn helper note */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Linkedin size={18} className="text-blue-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Import from LinkedIn Connections</p>
          <p className="text-xs text-blue-700 mt-1 leading-relaxed">
            You can export your LinkedIn connections as a CSV file. Go to{' '}
            <strong>LinkedIn → Settings &amp; Privacy → Data Privacy → Get a copy of your data</strong>,
            select <strong>Connections</strong>, and download the CSV. Then upload it here to import
            all your contacts at once.
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`relative flex flex-col items-center justify-center gap-4 border-2 border-dashed rounded-2xl p-12 transition-all duration-200 cursor-pointer
          ${isDragging ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50'}
          ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={handleFileInput}
        />

        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors
          ${isDragging ? 'bg-primary/10' : 'bg-muted'}`}>
          {isLoading ? (
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload size={28} className={isDragging ? 'text-primary' : 'text-muted-foreground'} />
          )}
        </div>

        <div className="text-center">
          <p className="text-base font-semibold text-foreground">
            {isLoading ? 'Parsing file…' : isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            or <span className="text-primary font-medium underline underline-offset-2">browse to upload</span>
          </p>
          <p className="text-xs text-muted-foreground mt-2">Supports CSV, XLSX, XLS · Max 10MB</p>
        </div>
      </div>

      {dragError && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle size={15} />
          {dragError}
        </div>
      )}

      {/* Format hints */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: FileText, label: 'LinkedIn CSV Export', desc: 'First Name, Last Name, Email, URL, Company, Position' },
          { icon: FileText, label: 'Custom CSV / Excel', desc: 'Any spreadsheet with contact columns — map them in the next step' },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label} className="flex items-start gap-3 p-3 bg-muted/40 rounded-xl border border-border">
            <Icon size={16} className="text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground">{label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
